<template>
  <div class="px-6 py-5 overflow-y-auto right-content-grid-vs h-[calc(100vh-309px)]">
      <div class="text-[#414141] font-sans">
        <h3 class="text-[1.25rem] leading-[1.75rem] mb-[1rem] font-semibold">Summary</h3>
      </div>
      <div v-if="isLoading" class="text-gray-600 font-sans py-4">
          <p>Loading data...</p>
      </div>
      <div v-else-if="errorMessage" class="p-4 bg-red-50 font-sans border border-red-200 rounded-md mb-4">
          <p class="text-red-700">{{ errorMessage }}</p>
      </div>
      <div v-else>
          <div class="fundraising-container">

              <!-- Annual Fundraising Goal Section -->
              <div class="goal-row">
                  <div class="left-col">
                      <div class="goal-label">Annual Fundraising Goal</div>
                      <div class="goal-box">${{ formatCurrency(fundingData.year_1_annual_fundraising_goal) }}</div>
                  </div>
                  <div class="right-col">
                      <div class="explanation">
                          <span class="equals">=</span>
                          <span class="text">Total Annual Operating Budget (<span class="highlight">Program Expenses</span> + <span class="highlight">Overhead Expenses</span>) – Other Revenue (<span class="highlight">Earned Revenue</span>, <span class="highlight">Investment Income</span>, <span class="highlight">Government Appropriations</span> & <span class="highlight">In-Kind</span>).</span>
                      </div>
                  </div>
              </div>

              <!-- Annual Fundraising Goal for 3 Years Section -->
              <div class="goal-row">
                  <div class="left-col">
                      <div class="goal-label">Annual Fundraising Goal for 3 Years</div>
                      <div class="goal-box">${{ formatCurrency(fundingData.total_annual_fundraising_goal_for_3_years) }}</div>
                  </div>
                  <div class="right-col">
                      <div class="explanation">
                          <span class="equals">=</span>
                          <span class="text">Annual Fundraising Goal for three (3) years.</span>
                      </div>
                  </div>
              </div>

              <!-- New Project Goal for 3 Years Section (Only show if module 9 is not skipped) -->
              <div v-if="!areProjectModulesSkipped" class="goal-row">
                  <div class="left-col">
                      <div class="symbol">+</div>
                      <div class="goal-label">New Project Goal for 3 Years</div>
                      <div class="goal-box">${{ formatCurrency(fundingData.total_new_project_fundraising_goal_for_3_years) }}</div>
                  </div>
                  <div class="right-col">
                      <div class="explanation">
                          <span class="equals">=</span>
                          <span class="text">This is the sum of your New Project Goals over three years.</span>
                      </div>
                  </div>
              </div>

              <!-- Total Fundraising Goal for 3 Years Section -->
              <div class="goal-row">
                  <div class="left-col">
                      <div v-if="!areProjectModulesSkipped" class="symbol">=</div>
                      <div class="goal-label">Total Fundraising Goal for 3 Years</div>
                      <div class="goal-box total">${{ formatCurrency(fundingData.final_total_fundraising_goal_for_3_years) }}</div>
                  </div>
                  <div class="right-col">
                      <div class="explanation">
                          <span class="equals">=</span>
                          <span class="text">This is the sum of your <span class="highlight">Annual Fundraising Goal</span> and your <span class="highlight">New Project Goal</span> over three years. <span class="note">*Rounded up to the closest $500K for your gift pyramid.</span></span>
                      </div>
                  </div>
              </div>
          </div>
      </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { useStepValidation } from '@/composables/useStepValidation';
import { useAuthStore } from '@/stores/auth';
import global from '@/global';

const isLoading = ref(true);
const errorMessage = ref("");
const areProjectModulesSkipped = ref(false);
const fundingData = ref({
  annual_operating_expenses_year_1: 0,
  year_1_annual_fundraising_goal: 0,
  year_2_annual_fundraising_goal: 0,
  year_3_annual_fundraising_goal: 0,
  total_annual_fundraising_goal_for_3_years: 0,
  new_project_expenses_for_3_years: 0,
  year_1_new_project_fundraising_goal: 0,
  year_2_new_project_fundraising_goal: 0,
  year_3_new_project_fundraising_goal: 0,
  total_new_project_fundraising_goal_for_3_years: 0,
  final_total_fundraising_goal_for_3_years: 0
});

const authStore = useAuthStore();

// Helper function to check if a route is skipped
const isRouteSkipped = (route, skippedRoutes) => {
  if (!Array.isArray(skippedRoutes) || typeof route !== 'string') {
    return false;
  }
  
  // Special case: Module 9 overview should not be considered skipped
  if (route === '/steps/funding-new-projects/overview') {
    return false;
  }
  
  // Check if the exact route is skipped or if it's part of a skipped module
  return skippedRoutes.some(skippedRoute => {
    // Check for exact match
    if (skippedRoute === route) return true;
    // Check if the current route is part of a skipped module path
    const moduleBase = route.split('/').slice(0, 3).join('/');
    return skippedRoute === moduleBase;
  });
};

// Helper function to format currency
const formatCurrency = (value) => {
  const numValue = value || 0;
  return numValue.toLocaleString();
};

onMounted(async () => {
  try {
    isLoading.value = true;
    errorMessage.value = "";
    
    // Initialize auth
    await authStore.fetchUserData();
    
    if (!authStore.user?.accessToken) {
      throw new Error('Unable to load user authentication. Please try refreshing the page.');
    }
    
    // Fetch skipped modules data
    const skippedModulesRes = await fetch(`${global.API_URL}/extensions/skipped_modules`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${authStore.user.accessToken}`,
        'Content-Type': 'application/json',
        'Cache-Control': 'no-cache, no-store'
      }
    });
    
    if (!skippedModulesRes.ok) {
      throw new Error(`Failed to fetch skipped modules. Status: ${skippedModulesRes.status}`);
    }
    
    const skippedModulesData = await skippedModulesRes.json();
    
    // Parse skipped routes from API response
    let skippedRoutes = [];
    if (skippedModulesData?.data?.skipped_routes && Array.isArray(skippedModulesData.data.skipped_routes)) {
      skippedRoutes = skippedModulesData.data.skipped_routes;
    } else if (skippedModulesData?.skipped_routes && Array.isArray(skippedModulesData.skipped_routes)) {
      skippedRoutes = skippedModulesData.skipped_routes;
    }
    
    // Check if project module 9 is skipped
    const module9Path = '/steps/funding-new-projects';
    
    areProjectModulesSkipped.value = isRouteSkipped(module9Path, skippedRoutes);
    
    // Fetch funding data
    const res = await fetch(`${global.API_URL}/total_fundraising_goal/total_fundraising_goal_for_3_years`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${authStore.user.accessToken}`,
      },
    });
    if (!res.ok) {
      throw new Error(`HTTP error! Status: ${res.status}`);
    }
    const data = await res.json();
    // Map API response to fundingData with null/undefined handling
    fundingData.value = {
      annual_operating_expenses_year_1: data.annual_operating_expenses_year_1 || 0,
      year_1_annual_fundraising_goal: data.year_1_annual_fundraising_goal || 0,
      year_2_annual_fundraising_goal: data.year_2_annual_fundraising_goal || 0,
      year_3_annual_fundraising_goal: data.year_3_annual_fundraising_goal || 0,
      total_annual_fundraising_goal_for_3_years: data.total_annual_fundraising_goal_for_3_years || 0,
      new_project_expenses_for_3_years: data.new_project_expenses_for_3_years || 0,
      year_1_new_project_fundraising_goal: data.year_1_new_project_fundraising_goal || 0,
      year_2_new_project_fundraising_goal: data.year_2_new_project_fundraising_goal || 0,
      year_3_new_project_fundraising_goal: data.year_3_new_project_fundraising_goal || 0,
      total_new_project_fundraising_goal_for_3_years: data.total_new_project_fundraising_goal_for_3_years || 0,
      final_total_fundraising_goal_for_3_years: data.final_total_fundraising_goal_for_3_years || 0
    };
  } catch (error) {
    errorMessage.value = error.message || 'Failed to load funding summary data.';
    console.error(error);
  } finally {
    isLoading.value = false;
  }
});

// Use the validation composable with default settings
useStepValidation();
</script>

<style scoped>
/* Inherit scrollbar styles from StepsNav component */
.right-content-grid-vs::-webkit-scrollbar {
    width: 8px;
}

.right-content-grid-vs::-webkit-scrollbar-track {
    background: #EBE8E1;
}

.right-content-grid-vs::-webkit-scrollbar-thumb {
    background: #00423D;
    border-radius: 6px;
}

.fundraising-container {
  margin: 0 auto;
  font-family: sans-serif;
}

.heading {
  font-size: 1.25rem;
  color: #00423D;
  margin-bottom: 2rem;
  font-weight: 600;
}

.goal-row {
  display: flex;
  align-items: center;
  padding: 1rem;
}

.left-col {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 40%;
}

.goal-label {
  font-size: 1rem;
  font-weight: 600;
  color: #00423D;
  margin-bottom: 0.75rem;
}

.goal-box {
  background-color: #E8F5E8;
  border: 1px solid #00423D;
  padding: 0.75rem 1.5rem;
  font-size: 1.25rem;
  font-weight: 600;
  color: #00423D;
  border-radius: 8px;
  text-align: center;
  min-width: 200px;
}

.goal-box.total {
  background-color: #E8F5E8;
  color: #00423D;
}

.symbol {
  font-size: 2.5rem;
  font-weight: 600;
  color: #00423D;
  margin-top: -1rem;
  margin-bottom: 0.75rem;
  text-align: center;
  width: 200px;
  margin-left: 0;
}

.right-col {
  width: 60%;
  display: flex;
  align-items: center;
}

.explanation {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.equals {
  font-size: 1.5rem;
  font-weight: 600;
  color: #00423D;
}

.text {
  font-size: 0.875rem;
  color: #00423D;
  line-height: 1.5;
}

.highlight {
  color: #00423D;
  font-weight: 600;
}

.note {
  font-style: italic;
  color: #00423D;
}
</style> 